<script setup lang="ts">
interface OnboardingStep {
  image: string
  title: string
  text: string
}

interface Props {
  steps: OnboardingStep[]
  title?: string
  auto?: boolean
  modalKey?: string
}

const props = withDefaults(defineProps<Props>(), {
  title: 'Découvrir les nouveautés',
  auto: false,
})

const emit = defineEmits<{
  close: []
}>()

const localePath = useLocalePath()
const currentStep = ref(0)
const isOpen = defineModel<boolean>('open', { default: true })

// Computed properties
const currentStepData = computed(() => props.steps[currentStep.value])
const isFirstStep = computed(() => currentStep.value === 0)
const isLastStep = computed(() => currentStep.value === props.steps.length - 1)
const progressPercentage = computed(() => ((currentStep.value + 1) / props.steps.length) * 100)

// Methods
function nextStep() {
  if (isLastStep.value) {
    closeModal()
  }
  else {
    currentStep.value++
  }
}

function previousStep() {
  if (!isFirstStep.value) {
    currentStep.value--
  }
}

function closeModal() {
  isOpen.value = false
  currentStep.value = 0
  emit('close')
}

function openModal() {
  isOpen.value = true
  currentStep.value = 0
}

// Keyboard navigation
function handleKeydown(event: KeyboardEvent) {
  if (!isOpen.value)
    return

  switch (event.key) {
    case 'ArrowRight':
    case ' ':
      event.preventDefault()
      nextStep()
      break
    case 'ArrowLeft':
      event.preventDefault()
      previousStep()
      break
    case 'Escape':
      event.preventDefault()
      closeModal()
      break
  }
}

// Expose methods for external use
defineExpose({
  open: openModal,
  close: closeModal,
})

// Add keyboard listener
onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
  plausible('show_onboarding_modal', { props: { name: props.modalKey } })

  // If auto mode is enabled, send ping to API and update user store
  if (props.auto && props.modalKey) {
    handleAutoModalView()
  }
})

// Handle auto modal view
async function handleAutoModalView() {
  if (!props.modalKey)
    return

  const config = useRuntimeConfig()
  const userStore = useUserStore()

  try {
    // Send ping to API
    await $fetch(`${config.public.apiUrl}/home/modal-view`, {
      method: 'POST',
      body: {
        modal_key: props.modalKey,
      },
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
    })

    // Add modal_key to user.viewed_onboarding_modals
    if (userStore.user && !userStore.user.viewed_onboarding_modals?.includes(props.modalKey)) {
      if (!userStore.user.viewed_onboarding_modals) {
        userStore.user.viewed_onboarding_modals = []
      }
      userStore.user.viewed_onboarding_modals.push(props.modalKey)
    }
  }
  catch (error) {
    console.warn('Failed to track modal view:', error)
  }
}

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<template>
  <UModal v-model:open="isOpen" :ui="{ content: 'max-w-md' }">
    <template #content>
      <div class="p-6">
        <!-- Header -->
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg font-semibold text-poivre">
            {{ title }}
          </h2>
          <UButton
            icon="i-mdi-close"
            color="neutral"
            variant="ghost"
            size="sm"
            @click="closeModal"
          />
        </div>

        <!-- Progress bar -->
        <div class="w-full bg-gray-200 rounded-full h-1 mb-6">
          <div
            class="bg-tomate h-1 rounded-full transition-all duration-300"
            :style="{ width: `${progressPercentage}%` }"
          />
        </div>

        <!-- Content -->
        <div class="text-center">
          <!-- Image -->
          <div class="mb-4">
            <img
              :src="currentStepData.image"
              :alt="currentStepData.title"
              class="w-80 h-80 mx-auto object-cover rounded-lg shadow-lg"
            >
          </div>

          <!-- Title -->
          <h3 class="text-xl font-semibold text-poivre mb-2">
            {{ currentStepData.title }}
          </h3>

          <!-- Text -->
          <p class="text-poivre-600 leading-tight">
            {{ currentStepData.text }}
          </p>

          <div v-if="currentStepData.button" class="mt-2">
            <UButton :to="localePath(currentStepData.button.to)" :icon="currentStepData.button.icon" color="secondary" variant="outline" :label="currentStepData.button.label" size="sm" @click="emit('close')" />
          </div>
        </div>

        <!-- Navigation -->
        <div class="flex items-center justify-between mt-4">
          <!-- Previous button container -->
          <div class="flex-1 flex justify-start">
            <UButton
              v-if="!isFirstStep"
              icon="i-mdi-arrow-left"
              color="primary"
              variant="outline"
              @click="previousStep"
            >
              Précédent
            </UButton>
          </div>

          <!-- Step indicator container -->
          <div class="flex-1 flex justify-center">
            <div class="flex space-x-1">
              <div
                v-for="(step, index) in steps"
                :key="index"
                class="w-2 h-2 rounded-full transition-colors duration-200"
                :class="index === currentStep ? 'bg-tomate' : 'bg-gray-300'"
              />
            </div>
          </div>

          <!-- Next/Close button container -->
          <div class="flex-1 flex justify-end">
            <UButton
              v-if="isLastStep"
              icon="i-mdi-check"
              color="primary"
              @click="emit('close')"
            >
              Fermer
            </UButton>

            <UButton
              v-else
              icon="i-mdi-arrow-right"
              color="primary"
              @click="nextStep"
            >
              Suivant
            </UButton>
          </div>
        </div>
      </div>
    </template>
  </UModal>
</template>
